import { CircleNotchIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { Button } from 'src/components/ui/button';

type WorkflowActiveConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True when the user chose to activate; false when pausing. */
  enabling: boolean;
  workflowName: string | undefined;
  onConfirm: () => Promise<void>;
  isPending: boolean;
};

export function WorkflowActiveConfirmDialog({
  open,
  onOpenChange,
  enabling,
  workflowName,
  onConfirm,
  isPending,
}: WorkflowActiveConfirmDialogProps) {
  const gt = useGT();
  const label = workflowName?.trim()
    ? `"${workflowName.trim()}"`
    : gt('this workflow', {
        context:
          'workflow name placeholder in sentences when the workflow has no name',
      });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {enabling ? (
              <T context="workflow active confirm dialog title">
                Turn workflow back on?
              </T>
            ) : (
              <T context="workflow pause confirm dialog title">
                Pause workflow?
              </T>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              {enabling ? (
                <p>
                  <T context="workflow activate confirm dialog body">
                    <Var>{label}</Var> will run again next time it is triggered.
                    Turning it on does not start a run immediately — only the
                    next schedule or trigger does.
                  </T>
                </p>
              ) : (
                <p>
                  <T context="workflow pause confirm dialog body">
                    <Var>{label}</Var> stays paused until you turn it back on.
                    Nothing new will start automatically; anything already
                    running keeps going.
                  </T>
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            <T context="dialog dismiss button">Cancel</T>
          </AlertDialogCancel>
          <Button
            type="button"
            variant="default"
            disabled={isPending}
            className="gap-2"
            onClick={async () => {
              await onConfirm();
            }}
          >
            {isPending ? (
              <CircleNotchIcon
                className="h-4 w-4 shrink-0 animate-spin"
                aria-hidden
              />
            ) : null}
            {enabling ? (
              <T context="workflow confirm activate primary button">Activate</T>
            ) : (
              <T context="workflow confirm pause primary button">Pause</T>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
